// ImageTextSelector — PowerToys "Text Extractor" style region OCR.
//
// Opens the source image fullscreen (same visual language as the modal's
// lightbox), lets the user drag a rectangle over a piece of text, then runs
// tesseract.js OCR on JUST that cropped region. PowerToys uses the native
// Windows.Media.Ocr engine which a browser can't reach; tesseract.js (WASM) is
// the in-browser equivalent of the same flow — select region → local OCR → text.
//
// The extracted text is handed back via onExtract(text); the caller decides
// where it lands (here: the "Change Image Text" original-text field + clipboard).
//
// Cropping is done from a fetched blob (object URL) rather than the live <img>
// so the canvas is never CORS-tainted when the image is served from a remote
// (Azure) origin without ACAO headers.

import { useState, useRef, useCallback, useEffect } from "react";
import { createPortal } from "react-dom";
import i18n from "i18next";
import { credentialsFor } from "../../../common/utils";
import { LuX, LuLoaderCircle } from "react-icons/lu";

// OCR the given pixel rectangle (in NATURAL image coordinates) out of imageUrl.
async function ocrRegion(imageUrl, rectNatural) {
  // Fetch → blob → object URL keeps the crop same-origin (no canvas taint).
  const resp = await fetch(imageUrl, { mode: "cors", ...credentialsFor(imageUrl) });
  if (!resp.ok) throw new Error(`fetch ${resp.status}`);
  const blob = await resp.blob();
  const bitmap = await createImageBitmap(blob);

  const w = Math.max(1, Math.round(rectNatural.width));
  const h = Math.max(1, Math.round(rectNatural.height));
  const canvas = document.createElement("canvas");
  // Upscale small selections a bit — tiny crops OCR poorly.
  const scale = Math.min(3, Math.max(1, 200 / Math.min(w, h)));
  canvas.width = Math.round(w * scale);
  canvas.height = Math.round(h * scale);
  const ctx = canvas.getContext("2d");
  ctx.imageSmoothingEnabled = true;
  ctx.imageSmoothingQuality = "high";
  ctx.drawImage(
    bitmap,
    Math.round(rectNatural.left),
    Math.round(rectNatural.top),
    w,
    h,
    0,
    0,
    canvas.width,
    canvas.height
  );
  if (typeof bitmap.close === "function") bitmap.close();
  const dataUrl = canvas.toDataURL("image/png");

  // Lazy-load tesseract so it never weighs on the editor's initial bundle.
  const { createWorker } = await import("tesseract.js");
  const worker = await createWorker("tur+eng");
  try {
    const { data } = await worker.recognize(dataUrl);
    return (data?.text || "").replace(/\s+/g, " ").trim();
  } finally {
    await worker.terminate();
  }
}

export default function ImageTextSelector({ imageUrl, onExtract, onClose }) {
  const imgRef = useRef(null);
  const [drag, setDrag] = useState(null); // { startX, startY, x, y } in viewport px
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Esc closes (unless mid-extraction).
  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape" && !loading) onClose?.();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [loading, onClose]);

  const onMouseDown = useCallback(
    (e) => {
      if (loading) return;
      e.preventDefault();
      setError(null);
      setDrag({ startX: e.clientX, startY: e.clientY, x: e.clientX, y: e.clientY });
    },
    [loading]
  );

  const onMouseMove = useCallback(
    (e) => {
      if (!drag) return;
      setDrag((d) => (d ? { ...d, x: e.clientX, y: e.clientY } : d));
    },
    [drag]
  );

  const onMouseUp = useCallback(async () => {
    if (!drag) return;
    const sel = {
      left: Math.min(drag.startX, drag.x),
      top: Math.min(drag.startY, drag.y),
      width: Math.abs(drag.x - drag.startX),
      height: Math.abs(drag.y - drag.startY),
    };
    setDrag(null);
    if (sel.width < 5 || sel.height < 5) return; // accidental click

    const img = imgRef.current;
    if (!img) return;
    const rect = img.getBoundingClientRect();
    // Intersect selection with the rendered image, then map to natural pixels.
    const left = Math.max(sel.left, rect.left);
    const top = Math.max(sel.top, rect.top);
    const right = Math.min(sel.left + sel.width, rect.right);
    const bottom = Math.min(sel.top + sel.height, rect.bottom);
    if (right <= left || bottom <= top) return;

    const scaleX = img.naturalWidth / rect.width;
    const scaleY = img.naturalHeight / rect.height;
    const rectNatural = {
      left: (left - rect.left) * scaleX,
      top: (top - rect.top) * scaleY,
      width: (right - left) * scaleX,
      height: (bottom - top) * scaleY,
    };

    setLoading(true);
    setError(null);
    try {
      const text = await ocrRegion(imageUrl, rectNatural);
      if (!text) {
        setError(i18n.t("NoTextDetected"));
        setLoading(false);
        return;
      }
      // Best-effort clipboard so the user can ctrl+v elsewhere too.
      try {
        await navigator.clipboard?.writeText(text);
      } catch {
        /* clipboard blocked (insecure context / permissions) — non-fatal */
      }
      onExtract?.(text);
    } catch (err) {
      console.error("[ImageTextSelector] OCR failed:", err);
      setError(i18n.t("TextExtractionFailed"));
      setLoading(false);
    }
  }, [drag, imageUrl, onExtract]);

  const selBox = drag
    ? {
        left: Math.min(drag.startX, drag.x),
        top: Math.min(drag.startY, drag.y),
        width: Math.abs(drag.x - drag.startX),
        height: Math.abs(drag.y - drag.startY),
      }
    : null;

  // Overlay document.body içine portallanır. Bu bileşen gömülü (embed) Aidea
  // Wizard panelinden de mount ediliyor; o panel bootstrap modalının
  // .modal-dialog düğümü içinde yaşıyor ve dialog transform aldığı için
  // position:fixed çocuklar viewport yerine dialog’a göre konumlanıyordu —
  // seçim dikdörtgeni ekranın başka bir yerine düşüyordu. Lightbox.jsx da
  // aynı yöntemi kullanıyor.
  return createPortal(
    <div
      onMouseDown={onMouseDown}
      onMouseMove={onMouseMove}
      onMouseUp={onMouseUp}
      style={{
        position: "fixed",
        inset: 0,
        background: "rgba(0,0,0,0.9)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        zIndex: 3000070, // above the dialog band (pageModal 3000000+) and Lightbox (3000060)
        cursor: loading ? "wait" : "crosshair",
        userSelect: "none",
      }}
    >
      <img
        ref={imgRef}
        src={imageUrl}
        alt="Select text"
        draggable={false}
        style={{
          maxWidth: "92vw",
          maxHeight: "88vh",
          objectFit: "contain",
          borderRadius: 8,
          pointerEvents: "none",
        }}
      />

      {/* Selection rectangle */}
      {selBox && selBox.width > 0 && selBox.height > 0 && (
        <div
          style={{
            position: "fixed",
            left: selBox.left,
            top: selBox.top,
            width: selBox.width,
            height: selBox.height,
            border: "2px solid #22c55e",
            background: "rgba(34,197,94,0.15)",
            pointerEvents: "none",
            zIndex: 3000071,
          }}
        />
      )}

      {/* Instruction / status banner */}
      <div
        style={{
          position: "fixed",
          top: 18,
          left: "50%",
          transform: "translateX(-50%)",
          background: "rgba(0,0,0,0.6)",
          color: "#fff",
          padding: "8px 16px",
          borderRadius: 8,
          fontSize: 13,
          fontWeight: 600,
          display: "flex",
          alignItems: "center",
          gap: 8,
          backdropFilter: "blur(4px)",
          zIndex: 3000072,
          pointerEvents: "none",
        }}
      >
        {loading ? (
          <>
            <LuLoaderCircle size={16} style={{ animation: "spin 1s linear infinite" }} />
            {i18n.t("ExtractingText")}
          </>
        ) : error ? (
          <span style={{ color: "#fca5a5" }}>{error}</span>
        ) : (
          i18n.t("DragOverTextToExtract")
        )}
      </div>

      {/* Close */}
      <button
        type="button"
        onClick={(e) => {
          e.stopPropagation();
          if (!loading) onClose?.();
        }}
        title={i18n.t("Cancel")}
        style={{
          position: "fixed",
          top: 18,
          right: 18,
          background: "rgba(255,255,255,0.15)",
          border: "none",
          borderRadius: "50%",
          width: 38,
          height: 38,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          color: "#fff",
          cursor: loading ? "not-allowed" : "pointer",
          backdropFilter: "blur(4px)",
          zIndex: 3000072,
        }}
      >
        <LuX size={18} />
      </button>
    </div>,
    document.body
  );
}
